/**
 * The "Did you mean …?" hint under the ad page's email field. A typo'd domain is
 * a real reader who never gets the guide, so the page offers the likely fix.
 * A hint only: it never blocks, and the address as typed can still be sent.
 */

// The domains most US readers over 50 actually use. A suggestion is only ever
// one of these, and none is suggested for another: the second half of the list
// is real domains one edit from the first (ymail.com is one letter from
// gmail.com), listed so they are never "corrected".
const COMMON_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "comcast.net",
  "msn.com",
  "live.com",
  "att.net",
  "sbcglobal.net",
  "verizon.net",
  "me.com",
  "bellsouth.net",
  "charter.net",
  "cox.net",
  "ymail.com",
  "mail.com",
  "mac.com",
  "aim.com",
  "gmx.com",
  "rocketmail.com",
  "protonmail.com",
  "proton.me",
  "earthlink.net",
  "juno.com",
  "frontier.com",
  "optonline.net",
];

/**
 * Levenshtein distance, capped: anything past `max` is just "too far". A swap
 * of two letters (gmial) counts as 2, which the cap of 2 still catches.
 */
function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = row;
  }
  return prev[b.length];
}

export function suggestEmail(email: string): string | null {
  const at = email.lastIndexOf("@");
  if (at < 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain.includes(".") || COMMON_DOMAINS.includes(domain)) return null;

  let best: string | null = null;
  let bestDistance = 3;
  for (const candidate of COMMON_DOMAINS) {
    const d = distance(domain, candidate, 2);
    if (d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  }
  return best && bestDistance <= 2 ? `${local}@${best}` : null;
}
